<script setup lang="ts">
import { Head, Link, usePage } from '@inertiajs/vue3'
import { computed } from 'vue'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import FocalImage from '@/Components/FocalImage.vue'
import { Button } from '@/Components/ui'
import { useTranslate } from '@/Composables/useTranslate'

const page = usePage()
const rentalEnabled = computed(() => Boolean((page.props as any)?.rental_enabled))

interface FeaturedProduct {
    id: number
    slug: string
    name: Record<string, string>
    short_description: Record<string, string>
    price_cents: number
    sale_percent?: number | null
    sale_price_cents?: number | null
    rental_price_cents_per_day: number | null
    is_rentable: boolean
    is_purchasable: boolean
    has_variants: boolean
    is_highlighted?: boolean
    highlight_text?: Record<string, string> | null
    currency: string
    image: string | null
    thumb: string | null
    focal_x: number
    focal_y: number
    focal_scale: number
    has_3d: boolean
    category_slug?: string | null
}

interface Service {
    id: number
    slug: string
    name: Record<string, string>
    price_net_cents: number
    price_from_cents: number | null
    tax_rate: number
    duration_minutes: number
}

defineProps<{
    featured: FeaturedProduct[]
    services: Service[]
    hero_image_url: string | null
    owner_photo_url?: string | null
    testimonials?: Array<{ id: number; rating: number | null; title: string | null; body: string | null; name: string; date: string | null }>
}>()

const { t, locale } = useTranslate()

function tr(map: Record<string, string>) {
    return map?.[locale.value] ?? map?.de ?? Object.values(map ?? {})[0] ?? ''
}
function fmt(c: number, currency = 'EUR') {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(c / 100)
}
function gross(net: number, rate: number) {
    return Math.round(net * (1 + rate / 100))
}
</script>

<template>
    <Head :title="t('Mikes Garage')" />
    <PublicLayout>
        <section class="relative overflow-hidden bg-gradient-to-tr from-white via-brand-50 to-brand-200 dark:from-gray-950 dark:via-gray-900 dark:to-brand-950">
            <div
                v-if="hero_image_url"
                class="pointer-events-none absolute inset-y-0 right-0 hidden w-[62%] bg-cover bg-center lg:block"
                :style="{
                    backgroundImage: `url(${hero_image_url})`,
                    maskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.55) 18%, black 38%), linear-gradient(to bottom, black 70%, transparent 100%)',
                    maskComposite: 'intersect',
                    WebkitMaskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.55) 18%, black 38%), linear-gradient(to bottom, black 70%, transparent 100%)',
                    WebkitMaskComposite: 'source-in',
                }"
            />
            <div class="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-white dark:to-gray-950"></div>
            <div class="relative px-6 py-14 sm:py-20 lg:px-12 lg:py-28">
                <div class="max-w-xl">
                    <span class="inline-block rounded-full bg-brand-600/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-brand-700 dark:text-brand-300">{{ $t('Öhringen Cappel') }}</span>
                    <h1 class="mt-4 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                        <span class="block">{{ $t('Bikes,') }}</span>
                        <span class="block text-brand-600">{{ $t('Zubehör') }}</span>
                        <span class="block">{{ $t('und Werkstatt.') }}</span>
                    </h1>
                    <p class="mt-5 text-base text-gray-700 dark:text-gray-300 sm:text-lg">
                        {{ $t('Hero subtitle') }}
                    </p>
                    <div class="mt-6 flex flex-wrap gap-3">
                        <Link href="/shop"><Button>{{ $t('Zum Shop') }}</Button></Link>
                        <Link href="/werkstatt/buchen"><Button variant="outline">{{ $t('Werkstatt-Termin') }}</Button></Link>
                        <Link v-if="rentalEnabled" href="/verleih"><Button variant="outline">{{ $t('Bike leihen') }}</Button></Link>
                    </div>
                    <div class="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-600 dark:text-gray-400">
                        <div class="flex items-center gap-2"><span>🛠</span> {{ $t('Eigene Werkstatt') }}</div>
                        <div class="flex items-center gap-2"><span>🔒</span> {{ $t('Zubehör & Ersatzteile') }}</div>
                        <div class="flex items-center gap-2"><span>💬</span> {{ $t('Persönliche Beratung') }}</div>
                    </div>
                </div>
            </div>
        </section>

        <section v-if="featured.length" class="px-6 py-16 lg:px-12">
            <div class="flex items-end justify-between">
                <div>
                    <h2 class="text-3xl font-bold">{{ $t('Aktuelle Deals') }} <span class="ml-1 rounded-full bg-red-600 px-2.5 py-0.5 text-lg font-black text-white align-middle">%</span></h2>
                    <p class="mt-1 text-sm text-gray-500">{{ $t('Mikes Empfehlungen — handverlesen, fair bepreist.') }}</p>
                </div>
                <Link href="/shop" class="hidden text-sm font-medium text-brand-600 hover:underline sm:block">{{ $t('Alle ansehen') }} →</Link>
            </div>
            <div class="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                <Link
                    v-for="p in featured"
                    :key="p.id"
                    :href="p.category_slug ? `/shop?category=${p.category_slug}` : `/shop/${p.slug}`"
                    class="group block overflow-hidden rounded-xl border border-gray-200 bg-white transition hover:-translate-y-1 hover:shadow-lg dark:border-gray-800 dark:bg-gray-900"
                >
                    <div class="relative px-6 py-4">
                        <FocalImage
                            :src="p.image || p.thumb"
                            :alt="tr(p.name)"
                            :focal-x="p.focal_x"
                            :focal-y="p.focal_y"
                            :focal-scale="p.focal_scale"
                            aspect="aspect-[4/3]"
                            fit="contain"
                        />
                        <span v-if="p.has_3d" class="absolute right-2 top-2 rounded-full bg-black/70 px-2 py-0.5 text-[10px] font-bold uppercase text-white">3D</span>
                        <span v-if="p.sale_percent" class="absolute left-2 top-2 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold text-white">−{{ p.sale_percent }}%</span>
                        <span v-if="p.is_highlighted && p.highlight_text" class="group absolute left-2 top-2 cursor-default rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-bold text-amber-900" :class="p.sale_percent ? 'top-7' : 'top-2'">
                            ★
                            <span class="pointer-events-none absolute bottom-full left-0 z-50 mb-1.5 hidden w-48 rounded-lg bg-gray-900 px-2.5 py-2 text-[11px] text-white shadow-xl group-hover:block">
                                {{ p.highlight_text?.[locale] || p.highlight_text?.de }}
                            </span>
                        </span>
                    </div>
                    <div class="p-4">
                        <h3 class="line-clamp-1 font-semibold">{{ tr(p.name) }}</h3>
                        <p v-if="tr(p.short_description)" class="mt-1 line-clamp-2 text-xs text-gray-500">{{ tr(p.short_description) }}</p>
                        <div class="mt-3 flex items-baseline justify-between">
                            <template v-if="p.is_purchasable">
                                <p v-if="p.sale_price_cents" class="flex items-baseline gap-2">
                                    <span class="text-lg font-bold text-red-600">
                                        <span v-if="p.has_variants" class="text-sm font-normal">ab </span>{{ fmt(p.sale_price_cents, p.currency) }}
                                    </span>
                                    <span class="text-sm text-gray-400 line-through">{{ fmt(p.price_cents, p.currency) }}</span>
                                </p>
                                <p v-else class="text-lg font-bold text-brand-600">
                                    <span v-if="p.has_variants" class="text-sm font-normal text-gray-500">ab </span>{{ fmt(p.price_cents, p.currency) }}
                                </p>
                            </template>
                            <p v-else-if="p.is_rentable && p.rental_price_cents_per_day" class="text-lg font-bold text-brand-600">{{ fmt(p.rental_price_cents_per_day, p.currency) }}<span class="text-xs font-normal text-gray-500"> {{ $t('/ Tag') }}</span></p>
                            <span v-if="p.is_rentable && p.is_purchasable && p.rental_price_cents_per_day" class="text-xs text-gray-500">{{ $t('leihbar') }}</span>
                        </div>
                    </div>
                </Link>
            </div>
            <Link href="/shop" class="mt-6 block text-center text-sm font-medium text-brand-600 hover:underline sm:hidden">{{ $t('Alle ansehen') }} →</Link>
        </section>

        <section class="bg-gray-50 px-6 py-16 dark:bg-gray-900 lg:px-12">
            <div class="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                <Link href="/shop" class="group rounded-2xl bg-white p-8 shadow-sm transition hover:shadow-xl dark:bg-gray-950">
                    <div class="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-100 text-2xl dark:bg-brand-900/40">🛒</div>
                    <h3 class="text-xl font-bold">{{ $t('Shop') }}</h3>
                    <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">{{ $t('Shop-Teaser') }}</p>
                    <p class="mt-4 text-sm font-semibold text-brand-600 group-hover:underline">{{ $t('Zum Shop') }} →</p>
                </Link>
                <Link href="/werkstatt" class="group rounded-2xl bg-white p-8 shadow-sm transition hover:shadow-xl dark:bg-gray-950">
                    <div class="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-100 text-2xl dark:bg-brand-900/40">🛠</div>
                    <h3 class="text-xl font-bold">{{ $t('Workshop') }}</h3>
                    <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">{{ $t('Workshop-Teaser') }}</p>
                    <p class="mt-4 text-sm font-semibold text-brand-600 group-hover:underline">{{ $t('Termin anfragen') }} →</p>
                </Link>
                <Link v-if="rentalEnabled" href="/verleih" class="group rounded-2xl bg-white p-8 shadow-sm transition hover:shadow-xl dark:bg-gray-950">
                    <div class="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-100 text-2xl dark:bg-brand-900/40">🚲</div>
                    <h3 class="text-xl font-bold">{{ $t('Rentals') }}</h3>
                    <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">{{ $t('Rentals-Teaser') }}</p>
                    <p class="mt-4 text-sm font-semibold text-brand-600 group-hover:underline">{{ $t('Bikes ansehen') }} →</p>
                </Link>
                <Link href="/leasing" class="group rounded-2xl bg-white p-8 shadow-sm transition hover:shadow-xl dark:bg-gray-950">
                    <div class="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-100 text-2xl dark:bg-brand-900/40">💼</div>
                    <h3 class="text-xl font-bold">{{ $t('Leasing & Dienstrad') }}</h3>
                    <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">{{ $t('Privat-Mietkauf, Firmen-Leasing oder Dienstrad-Leasing — finanziere dein Bike flexibel.') }}</p>
                    <p class="mt-4 text-sm font-semibold text-brand-600 group-hover:underline">{{ $t('Anfrage stellen') }} →</p>
                </Link>
            </div>
        </section>

        <section v-if="services.length" class="px-6 py-16 lg:px-12">
            <h2 class="text-3xl font-bold">{{ $t('Werkstatt-Leistungen') }}</h2>
            <p class="mt-1 text-sm text-gray-500">{{ $t('Faire Pauschalen, ehrliche Preise.') }}</p>
            <div class="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div v-for="s in services" :key="s.id" class="rounded-xl border border-gray-200 p-5 dark:border-gray-800">
                    <h3 class="font-semibold">{{ tr(s.name) }}</h3>
                    <p v-if="s.price_from_cents" class="mt-2 text-2xl font-bold text-brand-600">
                        <span class="text-sm font-normal text-gray-500">ab</span> {{ fmt(gross(s.price_from_cents, s.tax_rate)) }}
                    </p>
                    <p v-else class="mt-2 text-2xl font-bold text-brand-600">{{ fmt(gross(s.price_net_cents, s.tax_rate)) }}</p>
                    <p class="text-xs text-gray-500">~ {{ s.duration_minutes }} min</p>
                </div>
            </div>
            <div class="mt-6">
                <Link href="/werkstatt/buchen"><Button>{{ $t('Jetzt Termin buchen') }}</Button></Link>
            </div>
        </section>

        <section v-if="testimonials && testimonials.length" class="bg-gray-50 px-6 py-16 dark:bg-gray-900 lg:px-12">
            <div class="mx-auto max-w-6xl">
                <h2 class="text-3xl font-bold text-gray-900 dark:text-gray-100">{{ $t('Was Kunden sagen') }}</h2>
                <div class="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    <div v-for="r in testimonials" :key="r.id" class="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-200 dark:bg-gray-800 dark:ring-gray-700">
                        <div class="text-amber-400 text-lg">{{ '★'.repeat(r.rating || 5) }}<span class="text-gray-300 dark:text-gray-600">{{ '★'.repeat(5 - (r.rating || 5)) }}</span></div>
                        <h3 v-if="r.title" class="mt-2 font-semibold text-gray-900 dark:text-gray-100">{{ r.title }}</h3>
                        <p class="mt-1 text-sm text-gray-700 dark:text-gray-300 line-clamp-5">{{ r.body }}</p>
                        <p class="mt-3 text-xs text-gray-500 dark:text-gray-400">— {{ r.name }}{{ r.date ? ' · ' + r.date : '' }}</p>
                    </div>
                </div>
            </div>
        </section>

        <section class="bg-gray-100 px-6 py-16 text-gray-900 dark:bg-gray-900 dark:text-gray-100 lg:px-12">
            <div class="mx-auto grid max-w-6xl gap-10 md:grid-cols-[1fr_320px] md:items-center">
                <div>
                    <h2 class="text-3xl font-bold">Mike Sripoomma</h2>
                    <p class="mt-3 text-gray-700 dark:text-gray-300">{{ $t('About-paragraph-1') }}</p>
                    <p class="mt-3 text-gray-600 dark:text-gray-400">{{ $t('About-paragraph-2') }}</p>
                    <div class="mt-6 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200 dark:bg-gray-800 dark:ring-0">
                        <p class="text-sm font-semibold uppercase tracking-wider text-brand-600 dark:text-brand-400">{{ $t('Kontakt & Standort') }}</p>
                        <p class="mt-3 font-medium">Mikes Garage</p>
                        <p class="text-gray-700 dark:text-gray-300">74613 Öhringen Cappel</p>
                        <p class="mt-3 text-sm">
                            <a href="mailto:mike@mikes-garage.net" class="text-brand-600 hover:underline dark:text-brand-400">mike@mikes-garage.net</a>
                        </p>
                        <Link href="/kontakt" class="mt-4 inline-block">
                            <Button variant="outline">{{ $t('Kontakt aufnehmen') }}</Button>
                        </Link>
                    </div>
                </div>
                <div class="flex justify-center md:justify-end">
                    <div class="relative h-72 w-72 overflow-hidden rounded-2xl bg-gradient-to-br from-brand-100 to-brand-300 shadow-lg ring-1 ring-brand-200 dark:from-brand-900/40 dark:to-brand-700/40 dark:ring-brand-800">
                        <img v-if="owner_photo_url" :src="owner_photo_url" alt="Mike Sripoomma" class="h-full w-full object-cover" />
                        <div v-else class="flex h-full w-full flex-col items-center justify-center text-brand-700 dark:text-brand-200">
                            <span class="text-7xl font-bold">M</span>
                            <span class="mt-2 text-sm uppercase tracking-wider">Foto folgt</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </PublicLayout>
</template>
